import { apiClient } from '@copro/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import { isSupabaseError } from '../../../../constants/supabaseErrors';
import { useSpace } from '../../../../hooks/useSpace';
import { DocumentType } from '../../../../types';
import emptyHtmlToNull from '../../../../utils/emptyHtmlToNull';
import { keys as calendarKeys } from '../../../calendar/api/keys';
import { keys as documentKeys } from '../../../documents/api/keys';
import { useChangeDocumentsOfResource } from '../../../documents/hooks/useChangeDocumentsOfResource';
import { keys as topicKeys } from '../../../topics/api/keys';
import { keys as eventKeys } from '../../api/keys';
import { useGetEventById } from '../../api/useGetEventById';
import { usePostEvent } from '../../api/usePostEvent';
import { usePutEvent } from '../../api/usePutEvent';

import useGetProviderById from '../../../providers/api/useGetProviderById';

type Props = {
  onClose: () => void;
  subTopicId: string;

  eventId?: string;
  providerId?: string;
  initialValues?: Partial<z.infer<typeof formSchema>>;
  onSuccess?: () => void;
}

const documentsSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  file_url: z.string().nullable(),
  document_type_id: z.string().nullable().optional(),
  type: z.custom<DocumentType>().nullable().optional(),
  pendingFile: z.custom<File>().optional(),
})

const formSchema = z.object({
  title: z.string().trim().min(1, 'Ce champ est obligatoire'),
  description: z.string().trim().nullable().optional().transform(emptyHtmlToNull),
  date: z.date('Ce champ est obligatoire').transform(val => val.toISOString()),
  end_date: z.date().nullable().transform(val => val ? val.toISOString() : null),
  all_day: z.boolean().default(false),
  isImportant: z.boolean().default(false),
  isDisplayedInCalendar: z.boolean().default(true),
  documents: z.array(documentsSchema),
  provider_name: z.string().trim(),
  provider_id: z.string().trim().nullable(),
  contact_name: z.string().trim(),
  subject_id: z.string().min(1, 'Ce champ est obligatoire'),
}).refine(
  (data) => {
    if (!data.end_date) {
      return true;
    }

    return data.end_date >= data.date;
  },
  {
    message: "La date de fin ne peut pas être avant la date de début",
    path: ["end_date"],
  }
);

export default function useCreateUpdateEventModal(props: Props) {
  const { data: event, isLoading } = useGetEventById(props.eventId);
  const { data: provider } = useGetProviderById(props.providerId);
  const { mutateAsync: postEvent, isPending: isPostEventLoading } = usePostEvent();
  const { mutateAsync: putEvent, isPending: isPutEventLoading } = usePutEvent();
  const { uploadPendingFiles, saveDocumentChanges, isPending: isUpdateDocumentsPending } = useChangeDocumentsOfResource();

  const isFormReady = useRef<boolean>(props.eventId ? false : true);

  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      title: props.initialValues?.title ?? '',
      description: props.initialValues?.description ?? '',
      date: props.initialValues?.date ? new Date(props.initialValues.date) : new Date(),
      end_date: props.initialValues?.end_date ? new Date(props.initialValues.end_date) : null,
      all_day: props.initialValues?.all_day ?? true,
      isImportant: props.initialValues?.isImportant ?? false,
      isDisplayedInCalendar: props.initialValues?.isDisplayedInCalendar ?? true,
      provider_id: props.providerId ?? null,
      provider_name: props.initialValues?.provider_name ?? '',
      contact_name: props.initialValues?.contact_name ?? '',
      documents: props.initialValues?.documents ?? [],
      subject_id: props.subTopicId,
    },
    resolver: zodResolver(formSchema),
  });

  const [isModalToDeleteOpened, setIsModalToDeleteOpened] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showDescription, setShowDescription] = useState<boolean>(() => {
    return Boolean(props.initialValues?.description && props.initialValues.description.trim().length > 0);
  });
  const [showDocuments, setShowDocuments] = useState(false);

  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  useEffect(() => {
    if (event) {
      if (event.description && event.description.trim().length > 0) {
        setShowDescription(true);
      }

      form.reset({
        isImportant: !!event.isImportant,
        title: event.title,
        description: event.description,
        date: event.date ? new Date(event.date) : new Date(),
        end_date: event.end_date ? new Date(event.end_date) : null,
        all_day: !!event.all_day,
        isDisplayedInCalendar: !!event.isDisplayedInCalendar,
        provider_id: event.provider_id ?? null,
        provider_name: event.provider_name ?? '',
        contact_name: event.contact_name ?? '',
        documents: event.documents ?? [],
        subject_id: event.subject_id ?? undefined,
      })

      isFormReady.current = true;
    }
  }, [event]);

  useEffect(() => {
    if (provider?.name && !props.eventId && !form.getValues('provider_name')) {
      form.setValue('provider_id', provider.id);
      form.setValue('provider_name', provider.name);
    }
  }, [provider, props.eventId, form]);

  function afterConfirm(successMessage: string, targetSubTopicId?: string) {
    props.onSuccess?.();
    props.onClose();
    enqueueSnackbar(successMessage, 'success');
    queryClient.invalidateQueries({ queryKey: topicKeys.getHistoryFeedBySubTopicId(props.subTopicId) })
    queryClient.invalidateQueries({ queryKey: [...topicKeys.all, 'activityTimeline'] })
    queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(props.subTopicId) })
    if (targetSubTopicId && targetSubTopicId !== props.subTopicId) {
      queryClient.invalidateQueries({ queryKey: topicKeys.getHistoryFeedBySubTopicId(targetSubTopicId) })
      queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(targetSubTopicId) })
    }
    queryClient.invalidateQueries({ queryKey: calendarKeys.list() })
    if (currentSpace?.id) {
      queryClient.invalidateQueries({ queryKey: topicKeys.pendingSubTopics(currentSpace.id) })
    }
  }

  async function handlePost(formData: z.infer<typeof formSchema>) {
    setServerError(null);

    const { documents, ...data } = formData
    const targetSubTopicId = data.subject_id;

    try {
      // Upload files first — abort before creating the event if upload fails
      const uploadedDocuments = await uploadPendingFiles(documents, { subjectId: targetSubTopicId });

      const { id } = await postEvent({
        command: {
          ...data,
          space_id: currentSpace!.id,
        },
        subTopicId: targetSubTopicId,
      });

      if (id) {
        await saveDocumentChanges(
          event?.documents ?? [],
          uploadedDocuments,
          {
            event_id: id,
            sub_topic_id: targetSubTopicId
          }
        );
      } else {
        throw new Error('id not found');
      }

      afterConfirm('Événement créé.', targetSubTopicId);
    } catch (errors: unknown) {
      if (isSupabaseError(errors)) {
        setServerError(errors.message);

        return;
      }

      setServerError('Une erreur est survenue.')
    }
  }

  async function handlePut(formData: z.infer<typeof formSchema>) {
    setServerError(null);

    const { documents, ...data } = formData
    const targetSubTopicId = data.subject_id;

    try {
      // Upload files first — abort before updating the event if upload fails
      const uploadedDocuments = await uploadPendingFiles(documents, { subjectId: targetSubTopicId });

      const { id } = await putEvent({
        id: props.eventId!,
        command: data,
        subTopicId: targetSubTopicId
      });

      // Keep existing documents in sync with the new owner if the event changed sub-topic.
      if (targetSubTopicId !== props.subTopicId && props.eventId) {
        const { error: docsError } = await apiClient
          .from('documents')
          .update({ sub_topic_id: targetSubTopicId })
          .eq('event_id', props.eventId);

        if (docsError) throw docsError;
      }

      if (id) {
        await saveDocumentChanges(
          event?.documents ?? [],
          uploadedDocuments,
          {
            event_id: id,
            sub_topic_id: targetSubTopicId
          }
        );
      } else {
        throw new Error('id not found');
      }

      queryClient.invalidateQueries({ queryKey: eventKeys.detail(props.eventId!) })
      afterConfirm('Événement modifié.', targetSubTopicId);
    } catch (errors: unknown) {
      if (isSupabaseError(errors)) {
        setServerError(errors.message);

        return;
      }

      setServerError('Une erreur est survenue.')
    }
  }

  const description = form.watch('description');
  const documents = form.watch('documents');

  return {
    event,
    form,
    isFormReady: isFormReady.current && !isLoading,
    handleConfirm: props.eventId ? handlePut : handlePost,
    isConfirmationPending: isPostEventLoading || isPutEventLoading || isUpdateDocumentsPending,
    serverError,
    isModalToDeleteOpened,
    setIsModalToDeleteOpened,
    showDescription: showDescription || !!description,
    showDocuments: showDocuments || documents.length > 0,
    revealDescription: () => setShowDescription(true),
    revealDocuments: () => setShowDocuments(true),
  }
}

